import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useBackdropContext } from '../../../../../context/backdrop';
import { useInstallationContext } from '../../../../../context/installation';
import {
  ActivityType,
  BlindStateDirectionInternal,
  ChannelActionParameterInput,
  CreateAdvancedActionMutation,
  CreateAdvancedActionMutationVariables,
  EditActionMutation,
  EditActionMutationVariables,
  GateEventTypeInternal,
} from '../../../../../data-access/gql-types/graphql';
import { CREATE_ADVANCED_ACTION, EDIT_ACTION } from '../../../../../data-access/mutations/action';
import { useRefetchData } from '../../../../../hooks/refresh-data/use-refetch-data';
import { ROUTES } from '../../../../../routes/routes';
import {
  BlindStateTask,
  DimStateTask,
  GatePartialControlModeStateTask,
  GateDailyModeStateTask,
  GateStateTask,
  OnOffStateTask,
  RGBWStateTask,
} from '../../../../../types/actions';
import { toastSuccess } from '../../../../../utils/toast';
import { useLavvaCreateActionContext } from '../context';
import { DeviceTaskParams, GateActionInternal, SceneTaskType, TaskVariant } from '../types';

export const useActionTasks = () => {
  const navigate = useNavigate();
  const { actionId } = useParams<{ actionId: string }>();
  const { t } = useTranslation('action');
  const { selectedInstallationId } = useInstallationContext();
  const { name, icon, taskList, setTaskType, isDuplicate } = useLavvaCreateActionContext();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { refetchActions, refetchGroups } = useRefetchData();
  const [createAdvancedAction] = useMutation<CreateAdvancedActionMutation, CreateAdvancedActionMutationVariables>(
    CREATE_ADVANCED_ACTION,
  );
  const [editAction] = useMutation<EditActionMutation, EditActionMutationVariables>(EDIT_ACTION);

  useEffect(() => {
    if (!name) navigate(ROUTES.ActionList(), { replace: true });
  }, []);

  const selectTaskType = (taskType: SceneTaskType) => {
    setTaskType(taskType);

    switch (taskType) {
      case SceneTaskType.ChannelState:
        navigate(ROUTES.ActionTasksVariants());
        break;
      default:
        break;
    }
  };

  const addNewTask = () => {
    navigate(ROUTES.ActionTasksVariants());
  };

  const getBlindActivityByTaskAction = (action: BlindStateDirectionInternal) => {
    switch (action) {
      case BlindStateDirectionInternal.Open:
        return ActivityType.Open;
      case BlindStateDirectionInternal.Closed:
        return ActivityType.Close;
      case BlindStateDirectionInternal.Stopped:
        return ActivityType.Stop;
      default:
        return ActivityType.SetPosition;
    }
  };

  const getGateActivityByTaskAction = (action: GateActionInternal) => {
    switch (action) {
      case GateActionInternal.Open:
        return ActivityType.Open;
      case GateActionInternal.Closed:
        return ActivityType.Close;
      case GateActionInternal.Stopped:
        return ActivityType.Stop;
      case GateActionInternal.Tilt:
        return ActivityType.Tilt;
      default:
        return ActivityType.SetPosition;
    }
  };

  const getGateDailyModeActivityByTaskAction = (action: GateEventTypeInternal) => {
    switch (action) {
      case GateEventTypeInternal.SetDailyModeOn:
        return ActivityType.SetDailyModeOn;
      default:
        return ActivityType.SetDailyModeOff;
    }
  };

  const getGateControlModeActivityByTaskAction = (action: GateEventTypeInternal) => {
    switch (action) {
      case GateEventTypeInternal.SetPartialControlModeOn:
        return ActivityType.SetPartialControlModeOn;
      default:
        return ActivityType.SetPartialControlModeOff;
    }
  };

  const getParameter = (params: DeviceTaskParams): ChannelActionParameterInput => {
    switch (params.variant) {
      case TaskVariant.OnOff: {
        return { activityType: (params.state as OnOffStateTask).isOn ? ActivityType.On : ActivityType.Off, value: '' };
      }
      case TaskVariant.Dim: {
        return { activityType: ActivityType.SetDim, value: (params.state as DimStateTask).brightness.toString() };
      }
      case TaskVariant.SetRGBW: {
        const taskParams = params.state as RGBWStateTask;

        return {
          activityType: ActivityType.SetLightColor,
          value: `${taskParams.R},${taskParams.G},${taskParams.B}`,
        };
      }
      case TaskVariant.SetBlind: {
        const state = params.state as BlindStateTask<BlindStateDirectionInternal>;

        return {
          activityType: getBlindActivityByTaskAction(state.action),
          value: state.position?.toString() || '',
        };
      }
      case TaskVariant.SetGate: {
        const state = params.state as GateStateTask<GateActionInternal>;

        return {
          activityType: getGateActivityByTaskAction(state.action),
          value: state.position?.toString() || '',
        };
      }
      case TaskVariant.SetGateDailyMode: {
        const state = params.state as GateDailyModeStateTask<GateEventTypeInternal>;

        return {
          activityType: getGateDailyModeActivityByTaskAction(state.action),
          value: '',
        };
      }
      case TaskVariant.SetGatePartialControlMode: {
        const state = params.state as GatePartialControlModeStateTask<GateEventTypeInternal>;

        return {
          activityType: getGateControlModeActivityByTaskAction(state.action),
          value: '',
        };
      }
      case TaskVariant.SetVentilation: {
        return { activityType: ActivityType.Tilt, value: '' };
      }
    }

    return { activityType: ActivityType.On, value: '' };
  };

  const requestFinally = (message: string) => {
    refetchActions();
    refetchGroups();
    toastSuccess({ content: message });
  };

  const handleSubmit = () => {
    turnOnBackdrop();

    const taskChannels = taskList.map((x, i) => ({
      channelId: x.taskParams.channel.id,
      channelType: x.taskParams.channel.data.type,
      order: i,
      parameter: getParameter(x.taskParams),
    }));

    const actionBody = {
      installationId: selectedInstallationId,
      iconName: icon,
      name,
      channels: taskChannels,
    };

    if (!actionId) {
      createAdvancedAction({
        variables: {
          input: {
            ...actionBody,
          },
        },
        onCompleted: async (response) => {
          if (response?.createAdvancedAction.id) {
            await requestFinally(t('advanced.createSuccess'));
            turnOffBackdrop();
            navigate(ROUTES.ActionDetails(response?.createAdvancedAction?.id), {
              state: { back: !isDuplicate ? 3 : 4 },
            });
          } else turnOffBackdrop();
        },
        onError: () => turnOffBackdrop(),
      });
    } else {
      editAction({
        variables: {
          input: {
            id: actionId,
            ...actionBody,
          },
        },
        onCompleted: async (response) => {
          if (response?.editAction.idResponse?.id) {
            await requestFinally(t('advanced.editSuccess'));
            turnOffBackdrop();
            navigate(-2);
          } else turnOffBackdrop();
        },
        onError: () => turnOffBackdrop(),
      });
    }
  };

  return {
    addNewTask,
    handleSubmit,
    selectTaskType,
  };
};
